import React, { useEffect, useState, useRef, useCallback } from 'react';
import { motion } from 'framer-motion';
import LottieIllustration from './LottieIllustration';
import { loadAnimation } from '../utils/loadAnimation';

interface Service {
  id: number;
  title: string;
  description: string;
  icon: string;
  features: string[];
  animation?: any;
  animationKey?: string;
  animationPath?: string;
}

const Services: React.FC = () => {
  const [animations, setAnimations] = useState<{ [key: string]: any }>({});
  const [isLoading, setIsLoading] = useState(true);
  const [loadedAnimations, setLoadedAnimations] = useState<Set<string>>(new Set());
  const sectionRef = useRef<HTMLElement>(null);
  const cardsRef = useRef<HTMLDivElement>(null);
  const observerRef = useRef<IntersectionObserver | null>(null);

  // Lazy load animations when they come into view
  const loadAnimationLazy = useCallback(async (animationKey: string, animationPath: string) => {
    if (loadedAnimations.has(animationKey)) return;
    
    try {
      const animationData = await loadAnimation(animationPath);
      if (animationData) {
        setAnimations(prev => ({ ...prev, [animationKey]: animationData }));
        setLoadedAnimations(prev => new Set(Array.from(prev).concat(animationKey)));
      }
    } catch (error) {
      console.error(`Error loading animation ${animationKey}:`, error);
    }
  }, [loadedAnimations]);

  // Intersection Observer for lazy loading
  useEffect(() => {
    observerRef.current = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const animationKey = entry.target.getAttribute('data-animation-key');
            const animationPath = entry.target.getAttribute('data-animation-path');
            if (animationKey && animationPath) {
              loadAnimationLazy(animationKey, animationPath);
            }
          }
        });
      },
      { 
        rootMargin: '50px',
        threshold: 0.1 
      }
    );

    return () => {
      if (observerRef.current) {
        observerRef.current.disconnect();
      }
    };
  }, [loadAnimationLazy]);

  // Load critical animations immediately (first 3 services)
  useEffect(() => {
    const loadCriticalAnimations = async () => {
      try {
        const [chatBotAnimation, websiteAnimation, socialMediaAnimation, programmingAnimation, deliveryServiceAnimation, mobileAppAnimation, businessAnalysisAnimation, billingAnimation, groceryAnimation, ecommerceAnimation, homeAnimation, webDevelopmentAnimation, untitledAnimation] = await Promise.all([
          loadAnimation('/animations/Chat Bot Test.json'),
          loadAnimation('/animations/website.json'),
          loadAnimation('/animations/Social Media Marketing.json'),
          loadAnimation('/animations/Programming.json'),
          loadAnimation('/animations/delivery service.json'),
          loadAnimation('/animations/Mobile app development & concept user interface design uiux.json'),
          loadAnimation('/animations/Business Analysis.json'),
          loadAnimation('/animations/Billing icon.json'),
          loadAnimation('/animations/Grocery shopping bag pickup and delivery.json'),
          loadAnimation('/animations/shopping Ecommerce.json'),
          loadAnimation('/animations/Home.json'),
          loadAnimation('/animations/web development.json'),
          loadAnimation('/animations/movie ticket.json'),
        ]);

        setAnimations(prev => ({
          ...prev,
          chatBot: chatBotAnimation,
          website: websiteAnimation,
          socialMedia: socialMediaAnimation,
          programming: programmingAnimation,
          deliveryService: deliveryServiceAnimation,
          mobileApp: mobileAppAnimation,
          businessAnalysis: businessAnalysisAnimation,
          billing: billingAnimation,
          grocery: groceryAnimation,
          ecommerce: ecommerceAnimation,
          home: homeAnimation,
          webDevelopment: webDevelopmentAnimation,
          untitled: untitledAnimation
        }));

        setLoadedAnimations(prev => new Set(Array.from(prev).concat(['chatBot', 'website', 'socialMedia', 'programming', 'deliveryService', 'mobileApp', 'billing', 'grocery', 'ecommerce', 'home', 'webDevelopment', 'untitled'])));
        setIsLoading(false);
      } catch (error) {
        console.error('Error loading critical animations:', error);
        setIsLoading(false);
      }
    };

    loadCriticalAnimations();
  }, []);

  const services: Service[] = [
    {
      id: 1,
      title: 'AI Chatbots',
      description: 'Intelligent customer service automation',
      icon: '🤖',
      features: ['Natural Language Processing', '24/7 Support', 'Learning Capabilities'],
      animation: animations.chatBot,
      animationKey: 'chatBot',
      animationPath: '/animations/Chat Bot Test.json'
    },
    {
      id: 2,
      title: 'Web Applications',
      description: 'Custom web solutions for modern businesses',
      icon: '🌐',
      features: ['Responsive Design', 'Progressive Web App', 'SEO Optimized'],
      animation: animations.webDevelopment,
      animationKey: 'webDevelopment',
      animationPath: '/animations/web development.json'
    },
    {
      id: 3,
      title: 'Mobile Apps',
      description: 'Native and cross-platform mobile applications',
      icon: '📱',
      features: ['iOS & Android', 'Cross-platform', 'App Store Optimization'],
      animation: animations.socialMedia,
      animationKey: 'mobileApp',
      animationPath: '/animations/Mobile app development & concept user interface design uiux.json'
    },
    {
      id: 4,
      title: 'Digital Marketing',
      description: 'Comprehensive digital marketing strategies',
      icon: '📈',
      features: ['SEO/SEM', 'Social Media', 'Analytics & Reporting'],
      animation: animations.businessAnalysis,
      animationKey: 'socialMedia',
      animationPath: '/animations/Business Analysis.json'
    },
    {
      id: 6,
      title: 'Food Delivery Apps',
      description: 'Complete food delivery ecosystem with real-time tracking',
      icon: '🍕',
      features: ['Real-time Tracking', 'Restaurant Management', 'Driver App'],
      animation: animations.deliveryService,
      animationKey: 'deliveryService',
      animationPath: '/animations/delivery service.json'
    },
    {
      id: 5,
      title: 'E-commerce Solutions',
      description: 'Single and multi-vendor platforms with advanced features',
      icon: '🛒',
      features: ['Payment Integration', 'Inventory Management', 'Multi-vendor Support'],
      animation: animations.ecommerce,
      animationKey: 'ecommerce',
      animationPath: '/animations/shopping Ecommerce.json'
    },
    {
      id: 7,
      title: 'Grocery Delivery',
      description: 'On-demand grocery delivery with smart recommendations',
      icon: '🛍️',
      features: ['Smart Recommendations', 'Scheduled Delivery', 'Fresh Produce'],
      animation: animations.grocery,
      animationKey: 'grocery',
      animationPath: '/animations/Grocery shopping bag pickup and delivery.json'
    },
    {
      id: 8,
      title: 'Real Estate Platforms',
      description: 'Property listing and management solutions',
      icon: '🏠',
      features: ['Property Listings', 'Virtual Tours', 'Agent Management'],
      animation: animations.home,
      animationKey: 'home',
      animationPath: '/animations/Home.json'
    },
    {
      id: 9,
      title: 'Ticket Management',
      description: 'Event ticketing and booking systems',
      icon: '🎫',
      features: ['Event Booking', 'QR Code Tickets', 'Analytics Dashboard'],
      animation: animations.untitled,
      animationKey: 'untitled',
      animationPath: '/animations/movie ticket.json'
    },
    {
      id: 10,
      title: 'Billing Software',
      description: 'Comprehensive billing and invoicing solutions',
      icon: '💰',
      features: ['Automated Invoicing', 'Payment Tracking', 'Financial Reports'],
      animation: animations.billing,
      animationKey: 'billing',
      animationPath: '/animations/Billing icon.json'
    }
  ];


  return (
    <section ref={sectionRef} id="services" className="py-10 dark:bg-navy-light light:bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-16">
          <h2 className="text-4xl lg:text-5xl font-bold dark:text-white light:text-gray-900 mb-6">
            Our <span className="text-transparent bg-clip-text bg-gradient-to-r from-accent to-accent-light">Services</span>
          </h2>
          <p className="text-xl dark:text-gray-300 text-gray-600 max-w-3xl mx-auto">
            We provide comprehensive software solutions tailored to your business needs,
            from concept to deployment and beyond.
          </p>
        </div>

        <div>
          
        {/* Animation and Services Layout */}
        <div className="grid lg:grid-cols-2 gap-8 lg:gap-12 items-center mb-16">
          {/* Left Side - Animation */}
          <div className="order-1 lg:order-1">
            <div className="bg-gradient-to-br from-accent/5 to-accent-light/5 rounded-3xl p-6 lg:p-8">
              {animations.programming ? (
                <LottieIllustration
                  animationData={animations.programming}
                  className="w-full h-full"
                  width="100%"
                  height="300px"
                  loop={true}
                  autoplay={true}
                />
              ) : (
                <div className="flex items-center justify-center h-[300px]">
                  <div className="text-center">
                    <div className="text-6xl mb-4">💻</div>
                    <div className="text-lg font-semibold dark:text-white light:text-gray-900 mb-2">Programming</div>
                    <div className="text-sm dark:text-gray-200 light:text-gray-500">Custom software development</div>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Right Side - Services Description */}
          <div className="order-2 lg:order-2">
            <h3 className="text-2xl lg:text-3xl font-bold dark:text-white text-gray-900 mb-6">
              Comprehensive <span className="text-transparent bg-clip-text bg-gradient-to-r from-accent to-accent-light">Business Solutions</span>
            </h3>
            <p className="text-base lg:text-lg dark:text-gray-300 light:text-gray-600 mb-8 leading-relaxed">
              From e-commerce platforms to mobile applications, we deliver end-to-end solutions
              that drive business growth and enhance user experiences.
            </p>
            <div className="space-y-4">
              <div className="flex items-center">
                <div className="w-2 h-2 bg-accent rounded-full mr-3"></div>
                <span className="dark:text-gray-300 light:text-gray-600 text-sm lg:text-base">Custom software development</span>
              </div>
              <div className="flex items-center">
                <div className="w-2 h-2 bg-accent rounded-full mr-3"></div>
                <span className="dark:text-gray-300 light:text-gray-600 text-sm lg:text-base">Mobile app development</span>
              </div>
              <div className="flex items-center">
                <div className="w-2 h-2 bg-accent rounded-full mr-3"></div>
                <span className="dark:text-gray-300 light:text-gray-600 text-sm lg:text-base">Digital transformation</span>
              </div>
              <div className="flex items-center">
                <div className="w-2 h-2 bg-accent rounded-full mr-3"></div>
                <span className="dark:text-gray-300 light:text-gray-600 text-sm lg:text-base">Ongoing support & maintenance</span>
              </div>
            </div>
          </div>
        </div>
        </div>

        {/* Services Grid */}
        <div ref={cardsRef} className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
          {services.map((service, index) => (
            <motion.div
              key={service.id}
              initial={{ scale: 0, opacity: 0 }}
              whileInView={{ scale: 1, opacity: 1 }}
              viewport={{ once: false, margin: "-50px" }}
              transition={{ 
                duration: 0.3,
                delay: Math.min(index * 0.05, 0.3),
                type: "spring",
                stiffness: 120,
                damping: 20
              }}
              className="group"
            >
              <div 
                className="glass rounded-2xl p-6 h-full hover:bg-opacity-30 transition-all duration-300 transform hover:scale-105 hover:-translate-y-2 cursor-pointer relative"
                data-animation-key={service.animationKey}
                data-animation-path={service.animationPath}
                ref={(el) => {
                  if (el && observerRef.current && service.animationKey && service.animationPath) {
                    observerRef.current.observe(el);
                  }
                }}
              >
                {/* Icon or Animation */}
                <div className="mb-4 group-hover:scale-110 transition-transform duration-300">
                  {service.animation ? (
                    <div className="w-28 h-28 mx-auto flex items-center justify-center">
                      <LottieIllustration
                        animationData={service.animation}
                        className="w-full h-full"
                        width="112px"
                        height="112px"
                        loop={true}
                        autoplay={true}
                      />
                    </div>
                  ) : (
                    <div className="w-28 h-28 mx-auto flex items-center justify-center">
                      {/* Loading skeleton */}
                      <div className="w-full h-full bg-gradient-to-br from-gray-200 to-gray-300 dark:from-gray-700 dark:to-gray-600 rounded-2xl flex items-center justify-center animate-pulse">
                        <div className="text-4xl opacity-50">{service.icon}</div>
                      </div>
                    </div>
                  )}
                </div>

                {/* Title */}
                <h3 className="text-xl font-semibold dark:text-white light:text-gray-900 mb-3 group-hover:text-accent-light transition-colors duration-300">
                  {service.title}
                </h3>

                {/* Description */}
                <p className="dark:text-gray-300 light:text-gray-600 text-sm mb-4 leading-relaxed">
                  {service.description}
                </p>

                {/* Features */}
                <ul className="space-y-2">
                  {service.features.map((feature, featureIndex) => (
                    <li key={featureIndex} className="flex items-center text-sm dark:text-gray-400 text-gray-500">
                      <span className="w-1.5 h-1.5 bg-accent rounded-full mr-2 group-hover:bg-accent-light transition-colors duration-300"></span>
                      {feature}
                    </li>
                  ))}
                </ul>

                {/* Hover Effect Overlay */}
                <div className="absolute inset-0 bg-gradient-to-br from-accent/10 to-accent-light/10 rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none"></div>
              </div>
            </motion.div>
          ))}
        </div>

      </div>
    </section>
  );
};

export default Services;
